import React, { useEffect, useState } from 'react';
import { AppDispatch, RootState } from '../store';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAKSClusters } from '../store/slices/monitoringSlice';
import { Link } from 'react-router-dom';
import {
    ServerIcon,
    ChartBarIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    ClockIcon
} from '@heroicons/react/24/outline';

const KubernetesClustersPage = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { aksClusters, selectedAKSCluster, loading, error } = useSelector(
        (state: RootState) => state.monitoring
    );

    useEffect(() => {
        dispatch(fetchAKSClusters());
    }, [dispatch]);

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    <span className="ml-3 text-gray-600 dark:text-gray-400">Loading AKS clusters...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex items-center">
                        <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error loading clusters</h3>
                            <p className="mt-1 text-sm text-red-700 dark:text-red-300">{error}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Show selected cluster details if one is selected
    if (selectedAKSCluster) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        AKS Cluster Details
                    </h1>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Detailed information about {selectedAKSCluster.name}
                    </p>
                </div>

                {/* Cluster Overview Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <ServerIcon className="h-8 w-8 text-primary-600 dark:text-primary-400" />
                            <div className="ml-4">
                                <p className="text-sm text-gray-500 dark:text-gray-400">Nodes</p>
                                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                                    {selectedAKSCluster.readyNodes}/{selectedAKSCluster.nodeCount}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <ChartBarIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                            <div className="ml-4">
                                <p className="text-sm text-gray-500 dark:text-gray-400">CPU Usage</p>
                                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                                    {selectedAKSCluster.cpuUsage}%
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <ChartBarIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                            <div className="ml-4">
                                <p className="text-sm text-gray-500 dark:text-gray-400">Memory Usage</p>
                                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                                    {selectedAKSCluster.memoryUsage}%
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                        <div className="flex items-center">
                            {selectedAKSCluster.healthStatus === 'Healthy' ? (
                                <CheckCircleIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                            ) : (
                                <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                            )}
                            <div className="ml-4">
                                <p className="text-sm text-gray-500 dark:text-gray-400">Status</p>
                                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                                    {selectedAKSCluster.healthStatus}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Cluster Information */}
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden mb-6">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Cluster Information
                        </h2>
                    </div>
                    <div className="px-6 py-4">
                        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</dt>
                                <dd className="mt-1 text-sm text-gray-900 dark:text-white">{selectedAKSCluster.name}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Resource Group</dt>
                                <dd className="mt-1 text-sm text-gray-900 dark:text-white">{selectedAKSCluster.resourceGroup}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Location</dt>
                                <dd className="mt-1 text-sm text-gray-900 dark:text-white">{selectedAKSCluster.location}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Kubernetes Version</dt>
                                <dd className="mt-1 text-sm text-gray-900 dark:text-white">{selectedAKSCluster.kubernetesVersion}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">FQDN</dt>
                                <dd className="mt-1 text-sm text-gray-900 dark:text-white truncate">{selectedAKSCluster.fqdn}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Provisioning State</dt>
                                <dd className="mt-1">
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${selectedAKSCluster.provisioningState === 'Succeeded'
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                        }`}>
                                        {selectedAKSCluster.provisioningState}
                                    </span>
                                </dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Power State</dt>
                                <dd className="mt-1 text-sm text-gray-900 dark:text-white">{selectedAKSCluster.powerState}</dd>
                            </div>
                        </dl>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Quick Actions
                        </h2>
                    </div>
                    <div className="px-6 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Link
                                to="/kubernetes/workloads"
                                className="flex items-center p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
                            >
                                <ServerIcon className="h-8 w-8 text-primary-600 dark:text-primary-400" />
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">View Workloads</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Deployments, StatefulSets, DaemonSets</p>
                                </div>
                            </Link>

                            <Link
                                to="/kubernetes/dapr-components"
                                className="flex items-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                            >
                                <ChartBarIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">Dapr Components</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">View Dapr configuration</p>
                                </div>
                            </Link>

                            <Link
                                to="/monitoring/alerts"
                                className="flex items-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors"
                            >
                                <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">View Alerts</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Active cluster alerts</p>
                                </div>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Show all clusters if none selected
    return (
        <div className="container mx-auto px-4 py-6">
            <h1 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                Azure Kubernetes Service (AKS) Clusters
            </h1>

            {aksClusters.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
                    <ServerIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No AKS clusters found</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        No AKS clusters were found in your Azure subscription.
                    </p>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Nodes
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    CPU Usage
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Memory Usage
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Location
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {aksClusters.map((cluster) => (
                                <tr key={cluster.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                {cluster.name}
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                {cluster.resourceGroup}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${cluster.healthStatus === 'Healthy'
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                            }`}>
                                            {cluster.healthStatus}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                        {cluster.readyNodes}/{cluster.nodeCount}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="w-full">
                                            <div className="flex items-center">
                                                <div className="flex-1">
                                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                                        <div
                                                            className={`h-2 rounded-full ${cluster.cpuUsage > 80
                                                                    ? 'bg-red-500'
                                                                    : cluster.cpuUsage > 60
                                                                        ? 'bg-yellow-500'
                                                                        : 'bg-green-500'
                                                                }`}
                                                            style={{ width: `${cluster.cpuUsage}%` }}
                                                        ></div>
                                                    </div>
                                                </div>
                                                <span className="ml-2 text-xs text-gray-600 dark:text-gray-400">
                                                    {cluster.cpuUsage}%
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="w-full">
                                            <div className="flex items-center">
                                                <div className="flex-1">
                                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                                        <div
                                                            className={`h-2 rounded-full ${cluster.memoryUsage > 80
                                                                    ? 'bg-red-500'
                                                                    : cluster.memoryUsage > 60
                                                                        ? 'bg-yellow-500'
                                                                        : 'bg-blue-500'
                                                                }`}
                                                            style={{ width: `${cluster.memoryUsage}%` }}
                                                        ></div>
                                                    </div>
                                                </div>
                                                <span className="ml-2 text-xs text-gray-600 dark:text-gray-400">
                                                    {cluster.memoryUsage}%
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                        {cluster.location}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default KubernetesClustersPage;